const formulaStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  fontFamily: 'Georgia, Cambria, "Times New Roman", serif',
  fontSize: '1.08em',
  fontStyle: 'italic',
  lineHeight: 1.45,
  whiteSpace: 'nowrap',
};

const romanStyle = {
  fontStyle: 'normal',
};

const fractionStyle = {
  display: 'inline-flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  verticalAlign: 'middle',
  margin: '0 0.18em',
  fontSize: '0.95em',
  lineHeight: 1.1,
};

const numeratorStyle = {
  display: 'block',
  padding: '0 0.25em 0.12em',
  borderBottom: '1px solid currentColor',
};

const denominatorStyle = {
  display: 'block',
  padding: '0.12em 0.25em 0',
};

const subscriptWrapperStyle = {
  display: 'inline-flex',
  alignItems: 'baseline',
  gap: 1,
};

const subscriptStyle = {
  fontSize: '0.72em',
  lineHeight: 1,
  transform: 'translateY(0.28em)',
};

const superscriptWrapperStyle = {
  display: 'inline-flex',
  alignItems: 'flex-start',
  gap: 1,
};

const superscriptStyle = {
  fontSize: '0.72em',
  lineHeight: 1,
  transform: 'translateY(-0.28em)',
};

function cleanText(raw) {
  return raw.replace(/\\\s/g, ' ');
}

function readGroup(source, startIndex) {
  if (source[startIndex] !== '{') {
    return { content: source[startIndex] || '', endIndex: startIndex + 1 };
  }

  let depth = 0;
  for (let index = startIndex; index < source.length; index += 1) {
    const char = source[index];

    if (char === '{') {
      depth += 1;
    } else if (char === '}') {
      depth -= 1;
      if (depth === 0) {
        return {
          content: source.slice(startIndex + 1, index),
          endIndex: index + 1,
        };
      }
    }
  }

  return { content: source.slice(startIndex + 1), endIndex: source.length };
}

function readCommand(source, startIndex) {
  let index = startIndex + 1;

  if (source[index] && /\s/.test(source[index])) {
    while (source[index] && /\s/.test(source[index])) {
      index += 1;
    }
    return { command: 'space', endIndex: index };
  }

  while (source[index] && /[A-Za-z]/.test(source[index])) {
    index += 1;
  }

  return {
    command: source.slice(startIndex + 1, index),
    endIndex: index,
  };
}

function parseScript(source, index) {
  return source[index] === '{'
    ? readGroup(source, index)
    : { content: source[index] || '', endIndex: index + 1 };
}

function parseLatex(source, keyPrefix = 'formula') {
  const nodes = [];
  let index = 0;
  let key = 0;

  const nextKey = () => `${keyPrefix}-${key += 1}`;

  const pushText = (text) => {
    if (text) {
      nodes.push(text);
    }
  };

  const pushNode = (node) => {
    nodes.push(node);
  };

  while (index < source.length) {
    const char = source[index];

    if (char === '\\') {
      const { command, endIndex } = readCommand(source, index);
      index = endIndex;

      if (command === 'frac') {
        const numerator = readGroup(source, index);
        const denominator = readGroup(source, numerator.endIndex);
        index = denominator.endIndex;
        pushNode(
          <span key={nextKey()} style={fractionStyle}>
            <span style={numeratorStyle}>{parseLatex(numerator.content, `${keyPrefix}-num-${key}`)}</span>
            <span style={denominatorStyle}>{parseLatex(denominator.content, `${keyPrefix}-den-${key}`)}</span>
          </span>
        );
        continue;
      }

      if (command === 'mathrm' || command === 'text') {
        const group = readGroup(source, index);
        index = group.endIndex;
        pushNode(
          <span key={nextKey()} style={romanStyle}>
            {cleanText(group.content)}
          </span>
        );
        continue;
      }

      if (command === 'cdot') {
        pushText('·');
        continue;
      }

      if (command === 'tau') {
        pushText('τ');
        continue;
      }

      if (command === 'Delta') {
        pushText('Δ');
        continue;
      }

      if (command === 'ln') {
        pushNode(<span key={nextKey()} style={romanStyle}>ln</span>);
        continue;
      }

      if (command === 'left' || command === 'right') {
        continue;
      }

      if (command === 'space') {
        pushText(' ');
        continue;
      }

      pushText(command);
      continue;
    }

    if (char === '_') {
      const base = nodes.pop();
      const subscript = parseScript(source, index + 1);

      index = subscript.endIndex;
      pushNode(
        <span key={nextKey()} style={subscriptWrapperStyle}>
          {base}
          <sub style={subscriptStyle}>{parseLatex(subscript.content, `${keyPrefix}-sub-${key}`)}</sub>
        </span>
      );
      continue;
    }

    if (char === '^') {
      const base = nodes.pop();
      const superscript = parseScript(source, index + 1);

      index = superscript.endIndex;
      pushNode(
        <span key={nextKey()} style={superscriptWrapperStyle}>
          {base}
          <sup style={superscriptStyle}>{parseLatex(superscript.content, `${keyPrefix}-sup-${key}`)}</sup>
        </span>
      );
      continue;
    }

    if (/[A-Za-z]+/.test(char)) {
      let endIndex = index + 1;
      while (source[endIndex] && /[A-Za-z]/.test(source[endIndex])) {
        endIndex += 1;
      }
      pushText(source.slice(index, endIndex));
      index = endIndex;
      continue;
    }

    if (/\s/.test(char)) {
      pushText(' ');
      index += 1;
      continue;
    }

    if (char === '{' || char === '}') {
      index += 1;
      continue;
    }

    pushText(char);
    index += 1;
  }

  return nodes;
}

export default function LatexFormula({ source }) {
  return (
    <span style={formulaStyle}>
      {parseLatex(source)}
    </span>
  );
}
